/**
 * Papunet 2026 user survey — shared date/cookie helpers (matches WP papunet theme).
 */

export const KYSELY_URLS = {
  fi: 'https://link.webropol.com/s/papunetin-kayttajakysely-2026',
  sv: 'https://link.webropol.com/s/papunets-anvandarenkat-2026',
}

export const KYSELY_COOKIES = {
  fi: 'kysely-cookie-papunet-2026-10-fi',
  sv: 'kysely-cookie-papunet-2026-10-se',
}

const WINDOW_START = '2026-10-27 00:00:01'
const WINDOW_END = '2026-11-15 23:59:59'

function formatHelsinkiDateTime(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Helsinki',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date)

  const get = (type) => parts.find((p) => p.type === type)?.value ?? '00'

  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get(
    'minute'
  )}:${get('second')}`
}

/** Same window as zek_date_nosto( alku/loppu ) in Europe/Helsinki. */
export function isKyselyCampaignActive(now = new Date()) {
  const helsinkiNow = formatHelsinkiDateTime(now)
  return helsinkiNow >= WINDOW_START && helsinkiNow <= WINDOW_END
}

/** Map app lang codes (fi | sv | se) to survey language. */
export function normalizeKyselyLang(lang) {
  if (lang === 'sv' || lang === 'se') {
    return 'sv'
  }
  return 'fi'
}

export function getKyselyCookieName(lang) {
  return KYSELY_COOKIES[normalizeKyselyLang(lang)]
}

export function getKyselyUrl(lang) {
  return KYSELY_URLS[normalizeKyselyLang(lang)]
}

export function hasKyselyDismissCookie(cookieName) {
  if (typeof document === 'undefined') {
    return false
  }
  return Boolean(
    document.cookie.split('; ').find((row) => row.startsWith(`${cookieName}=`))
  )
}

/**
 * Cookie expiry aligned with WP content-kysely.php + kysely.js (UTC end of campaign date).
 */
export function getKyselyCookieExpiresUtcString() {
  return new Date(Date.UTC(2026, 10, 15, 23, 59, 59)).toUTCString()
}

export function setKyselyDismissCookie(cookieName) {
  const expires = getKyselyCookieExpiresUtcString()
  document.cookie = `${cookieName}=true; path=/; expires=${expires}; SameSite=None; Secure`
}

/** Hostname-based lang when app has no i18n (e.g. Papuri). */
export function detectKyselyLangFromHostname() {
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  if (host === 'bildbank.papunet.net' || host === 'bildverktyg.papunet.net') {
    return 'sv'
  }
  return 'fi'
}

/**
 * Production hosts where the Papunet user survey may appear (direct visits only).
 * SV twins included; localhost for local dev.
 */
export const KYSELY_ALLOWED_HOSTNAMES = [
  'kuvapankki.papunet.net',
  'bildbank.papunet.net',
  'kuvatyokalu.papunet.net',
  'bildverktyg.papunet.net',
  'papuri.papunet.net',
  'localhost',
  '127.0.0.1',
]

export function isKyselyAllowedHostname(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  return KYSELY_ALLOWED_HOSTNAMES.includes(hostname)
}

/**
 * Survey only on top-level tool pages, not when embedded (e.g. Papuri in papunet.net pelit).
 * External referrer (papunet.net, search, etc.) → hide; same-host navigation → OK.
 */
export function isKyselyDirectToolVisit(
  win = typeof window !== 'undefined' ? window : undefined
) {
  if (!win?.location) {
    return false
  }
  if (win.self !== win.top) {
    return false
  }
  const referrer = win.document?.referrer ?? ''
  if (!referrer) {
    return true
  }
  try {
    return new URL(referrer).hostname === win.location.hostname
  } catch {
    return false
  }
}

/** Campaign window + allowed host + not embedded / not referred from papunet.net pelit. */
export function shouldShowKyselySurvey(
  now = new Date(),
  win = typeof window !== 'undefined' ? window : undefined
) {
  if (!isKyselyCampaignActive(now)) {
    return false
  }
  if (!win?.location) {
    return false
  }
  if (!isKyselyAllowedHostname(win.location.hostname)) {
    return false
  }
  return isKyselyDirectToolVisit(win)
}

export const KYSELY_COPY = {
  fi: {
    heading: 'Papunetin käyttäjäkysely',
    firstParagraph: 'Vastaa kyselyyn ja kerro, mitä mieltä olet Papunetistä.',
    secondParagraph: 'Vastauksesi on meille tärkeä, kiitos!',
    link: 'Siirry kyselyyn tästä linkistä.',
    dontShowAgain: 'Älä näytä kyselyä uudelleen.',
    close: 'Sulje',
    closeAria: 'Sulje',
  },
  sv: {
    heading: 'Papunets användarenkät',
    firstParagraph: 'Svara på enkäten och berätta för oss vad du tycker om Papunet.',
    secondParagraph: 'Dina svar är viktiga för oss. Tack!',
    link: 'Gå till enkäten via denna länk.',
    dontShowAgain: 'Visa inte enkäten igen.',
    close: 'Stäng',
    closeAria: 'Stäng',
  },
}
